import React from "react";
import { IconButton } from "@mui/material";
import { Phone, Email, LocationOn, Instagram, Facebook, WhatsApp } from "@mui/icons-material";
import { Link } from "react-router-dom";

const Footer = () => {
    // Standard WhatsApp Click-to-Chat API link with encoded greeting message
    const whatsappUrl = "https://wa.me/254700870225?text=Hello%20Eagle%20Skyline%20Solutions%2C%20I%20have%20an%20inquiry.";

    return (
        <footer className="bg-slate-900 text-gray-300 pt-12 pb-6 border-t-4 border-[#DE8F07]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">

                    {/* Brand & Socials Section */}
                    <div>
                        <h3 className="text-xl font-bold text-white mb-4 tracking-wide">
                            EAGLE SKYLINE <span className="text-[#DE8F07]">SOLUTIONS</span>
                        </h3>
                        <p className="text-sm text-gray-400 leading-relaxed mb-4">
                            Your trusted destination for quality products, reliable services, and seamless shopping experiences.
                        </p>

                        {/* Social Media Links */}
                        <div className="flex gap-3">
                            <a
                                href="https://www.instagram.com/Eagle_Skyline_Solutions"
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Instagram"
                            >
                                <IconButton
                                    className="bg-slate-800 text-white hover:text-white hover:bg-[#DE8F07] transition-all duration-200"
                                    size="small"
                                >
                                    <Instagram sx={{ fontSize: 20, color: '#ffffff' }} />
                                </IconButton>
                            </a>
                            <a
                                href="https://www.facebook.com/profile.php?id=61569229552470"
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Facebook"
                            >
                                <IconButton
                                    className="bg-slate-800 text-white hover:text-white hover:bg-[#DE8F07] transition-all duration-200"
                                    size="small"
                                >
                                    <Facebook sx={{ fontSize: 20, color: '#ffffff' }} />
                                </IconButton>
                            </a>
                            <a
                                href={whatsappUrl}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="WhatsApp"
                            >
                                <IconButton
                                    className="bg-slate-800 text-white hover:text-white hover:bg-[#25D366] transition-all duration-200"
                                    size="small"
                                >
                                    <WhatsApp sx={{ fontSize: 20, color: '#ffffff' }} />
                                </IconButton>
                            </a>
                        </div>
                    </div>

                    {/* Quick Navigation Links */}
                    <div>
                        <h4 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
                            Quick Links
                        </h4>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Home
                                </Link>
                            </li>
                            <li>
                                <Link to="/products" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Shop
                                </Link>
                            </li>
                            <li>
                                <Link to="/account/orders" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    My Account
                                </Link>
                            </li>
                            <li>
                                <Link to="/cart" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Cart
                                </Link>
                            </li>
                            <li>
                                <Link to="/wishlist" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Wishlist
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Customer Support */}
                    <div>
                        <h4 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
                            Customer Support
                        </h4>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/help" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Help Center / FAQs
                                </Link>
                            </li>
                            <li>
                                <Link to="/shipping" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Shipping Policy
                                </Link>
                            </li>
                            <li>
                                <Link to="/returns" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Returns & Exchanges
                                </Link>
                            </li>
                            <li>
                                <Link to="/terms" className="hover:text-[#DE8F07] transition-colors duration-150">
                                    Terms & Conditions
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h4 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
                            Contact Us
                        </h4>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start gap-3">
                                <Phone sx={{ fontSize: 18, color: '#DE8F07' }} className="mt-0.5" />
                                <div>
                                    <span className="block text-xs text-gray-400">Hotline</span>
                                    <a href="tel:0700870225" className="font-medium hover:text-[#DE8F07]">
                                        0700870225
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <WhatsApp sx={{ fontSize: 18, color: '#25D366' }} className="mt-0.5" />
                                <div>
                                    <span className="block text-xs text-gray-400">WhatsApp Chat</span>
                                    <a
                                        href={whatsappUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="font-medium hover:text-[#25D366]"
                                    >
                                        Chat on WhatsApp
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Email sx={{ fontSize: 18, color: '#DE8F07' }} className="mt-0.5" />
                                <div>
                                    <span className="block text-xs text-gray-400">Email</span>
                                    <a href="mailto:info@eagleskylinesolutions.co.ke" className="font-medium hover:text-[#DE8F07]">
                                        info@eagleskylinesolutions.co.ke
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <LocationOn sx={{ fontSize: 18, color: '#DE8F07' }} className="mt-0.5" />
                                <span className="text-sm">
                                    Nairobi, Kenya
                                </span>
                            </li>
                        </ul>
                    </div>

                </div>

                {/* Divider & Copyright */}
                <div className="border-t border-slate-800 pt-6 mt-6 text-center text-xs text-gray-500">
                    <p>
                        &copy; {new Date().getFullYear()} Cyphercom Experts. All rights reserved.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;